import React, { useState } from 'react';

interface AlertRule {
  id: string;
  productId?: string;
  category?: string;
  threshold: number;
  severity: 'info' | 'warning' | 'critical';
  ruleType: 'stock_level' | 'price_change' | 'expiry_days' | 'custom';
  isActive: boolean;
}

interface AlertRulesManagerProps {
  rules: AlertRule[];
  products: Array<{ id: string; name: string }>;
  onCreateRule: (rule: any) => void;
  onUpdateRule: (ruleId: string, updates: any) => void;
  onDeleteRule: (ruleId: string) => void;
}

export function AlertRulesManager({
  rules,
  products,
  onCreateRule,
  onUpdateRule,
  onDeleteRule,
}: AlertRulesManagerProps) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    productId: '',
    threshold: 5,
    severity: 'warning' as const,
    ruleType: 'stock_level' as const,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreateRule(formData);
    setFormData({
      productId: '',
      threshold: 5,
      severity: 'warning',
      ruleType: 'stock_level',
    });
    setShowForm(false);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600 bg-red-50';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-blue-600 bg-blue-50';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-gray-900">Alert Rules</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          {showForm ? 'Cancel' : 'New Rule'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 border border-gray-200 rounded-lg">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
              <select
                value={formData.productId}
                onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Threshold</label>
              <input
                type="number"
                value={formData.threshold}
                onChange={(e) => setFormData({ ...formData, threshold: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rule Type</label>
              <select
                value={formData.ruleType}
                onChange={(e) => setFormData({ ...formData, ruleType: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="stock_level">Stock Level</option>
                <option value="price_change">Price Change</option>
                <option value="expiry_days">Expiry Days</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            Create Rule
          </button>
        </form>
      )}

      {rules.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No alert rules defined</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="text-left p-3 font-semibold">Product</th>
                <th className="text-center p-3 font-semibold">Rule Type</th>
                <th className="text-center p-3 font-semibold">Threshold</th>
                <th className="text-center p-3 font-semibold">Severity</th>
                <th className="text-center p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} className="border-b hover:bg-gray-50">
                  <td className="p-3">{rule.productId || 'All Products'}</td>
                  <td className="p-3 text-center text-xs font-mono">{rule.ruleType}</td>
                  <td className="p-3 text-center">{rule.threshold}</td>
                  <td className="p-3 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getSeverityColor(rule.severity)}`}>
                      {rule.severity}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <button
                      onClick={() => onDeleteRule(rule.id)}
                      className="px-3 py-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
